import { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useSEO } from "../hooks/useSEO";
import { createOrder } from "../api";
import { money, shippingFor } from "../config/brand";

const FIELDS = [
  { name: "name", label: "FULL NAME", type: "text", required: true },
  { name: "email", label: "EMAIL", type: "email", required: true },
  { name: "phone", label: "PHONE", type: "tel", required: false },
  { name: "address", label: "SHIPPING ADDRESS", type: "text", required: true },
  { name: "city", label: "CITY", type: "text", required: true },
  { name: "state", label: "STATE / PROVINCE", type: "text", required: false },
  { name: "country", label: "COUNTRY", type: "text", required: true },
  { name: "postal_code", label: "POSTAL CODE", type: "text", required: true },
];

export default function CheckoutPage() {
  const { items, subtotal, clear } = useCart();
  const [form, setForm] = useState({});
  const [placing, setPlacing] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  useSEO("CHECKOUT — RICH KIDS FUNERAL", "RICH KIDS FUNERAL checkout.");

  const shipping = shippingFor(subtotal);

  const submit = async (e) => {
    e.preventDefault();
    setPlacing(true);
    setError("");
    try {
      const res = await createOrder({
        customer: form,
        items: items.map((i) => ({ slug: i.slug, size: i.size, qty: i.qty })),
      });
      setOrder(res);
      clear();
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setPlacing(false);
    }
  };

  if (order)
    return (
      <div className="px-4 py-32 text-center max-w-xl mx-auto" data-testid="order-success">
        <p className="text-[10px] tracking-[0.25em] text-black/40">ORDER PLACED</p>
        <h1 className="font-anton-sc text-3xl md:text-5xl tracking-[0.08em] mt-4">
          {order.order_number}
        </h1>
        <p className="text-sm text-black/60 mt-6 leading-relaxed">
          Thank you. Your order has been received — a confirmation will be sent
          to {order.customer.email}. Payment integration connects at this step.
        </p>
        <div className="mt-8 border-t border-black/10 pt-6 text-left space-y-2 text-[12px]">
          {order.items.map((i) => (
            <div key={i.slug + i.size} className="flex justify-between">
              <span>
                {i.name} {i.size && `— ${i.size}`} × {i.qty}
              </span>
              <span>{money(i.line_total)}</span>
            </div>
          ))}
          <div className="flex justify-between pt-3 border-t border-black/10 font-medium">
            <span>Total</span>
            <span data-testid="order-total">{money(order.total)}</span>
          </div>
        </div>
        <Link
          to="/all-products"
          data-testid="order-continue-shopping"
          className="inline-block mt-10 text-[11px] font-medium tracking-[0.2em] border border-black px-10 py-4 hover:bg-black hover:text-white transition-colors"
        >
          CONTINUE SHOPPING
        </Link>
      </div>
    );

  if (items.length === 0)
    return (
      <div className="px-4 py-32 text-center" data-testid="checkout-empty">
        <p className="text-[11px] tracking-[0.2em] text-black/50">YOUR CART IS EMPTY</p>
        <Link
          to="/all-products"
          className="inline-block mt-8 text-[11px] font-medium tracking-[0.2em] border border-black px-10 py-4 hover:bg-black hover:text-white transition-colors"
        >
          CONTINUE SHOPPING
        </Link>
      </div>
    );

  return (
    <div className="px-3 md:px-6 pt-8 md:pt-12 max-w-5xl" data-testid="checkout-page">
      <h1 className="font-anton-sc text-2xl md:text-4xl tracking-[0.08em]">CHECKOUT</h1>
      <form onSubmit={submit} className="grid md:grid-cols-[1fr_360px] gap-12 mt-10">
        <div className="space-y-5">
          {FIELDS.map((f) => (
            <div key={f.name}>
              <label
                htmlFor={`checkout-${f.name}`}
                className="block text-[10px] tracking-[0.25em] text-black/40 mb-2"
              >
                {f.label}
                {f.required ? "" : " (OPTIONAL)"}
              </label>
              <input
                id={`checkout-${f.name}`}
                data-testid={`checkout-${f.name}`}
                type={f.type}
                required={f.required}
                value={form[f.name] || ""}
                onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                className="w-full border border-black/25 px-4 py-3.5 text-sm bg-transparent focus:border-black focus:outline-none transition-colors"
              />
            </div>
          ))}
          {error && (
            <p className="text-[12px] text-[#e50000]" data-testid="checkout-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={placing}
            data-testid="place-order-button"
            className="w-full bg-black text-white text-[11px] font-medium tracking-[0.2em] py-4 hover:bg-[#e50000] transition-colors disabled:opacity-50"
          >
            {placing ? "PLACING ORDER…" : "PLACE ORDER"}
          </button>
          <p className="text-[10px] tracking-[0.1em] text-black/40 leading-relaxed">
            Payment provider connects at this step — no payment is processed yet.
          </p>
        </div>
        <div className="self-start border border-black/15 p-6" data-testid="checkout-summary">
          <p className="text-[11px] font-medium tracking-[0.2em] mb-5">ORDER SUMMARY</p>
          <div className="space-y-4">
            {items.map((i) => (
              <div key={i.key} className="flex gap-3">
                <div className="w-14 aspect-[1/1] bg-white overflow-hidden flex-shrink-0">
                  {i.image && (
                    <img src={i.image} alt={i.name} className="w-full h-full object-contain" loading="lazy" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-medium tracking-[0.06em] uppercase leading-snug">
                    {i.name}
                  </p>
                  <p className="text-[10px] text-black/50 mt-0.5">
                    {i.size && `SIZE ${i.size} · `}QTY {i.qty}
                  </p>
                </div>
                <p className="text-[11px]">{money(i.price * i.qty)}</p>
              </div>
            ))}
          </div>
          <div className="space-y-2 text-[12px] mt-6 pt-4 border-t border-black/10">
            <div className="flex justify-between">
              <span className="text-black/60">Subtotal</span>
              <span data-testid="checkout-subtotal">{money(subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-black/60">Shipping</span>
              <span data-testid="checkout-shipping">{shipping === 0 ? "FREE" : money(shipping)}</span>
            </div>
            <div className="flex justify-between pt-3 border-t border-black/10 font-medium">
              <span>Total</span>
              <span data-testid="checkout-total">{money(subtotal + shipping)}</span>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
